import qs from 'qs';
import { resolveRelations, type ResolvablePath } from '@/models/resolve';

export type SortDirection = 'asc' | 'desc';

/** Tailles de page proposées sous les listes (la première par défaut, comme l'API) */
export const PAGE_SIZES = [10, 25, 50] as const;

/** Une page d'une ressource API Platform : filtres, tri (order[champ]), numéro de page et taille */
export interface PageRequest {
  /** Une liste envoie le filtre en tableau (timeSheetStatus[]=2&timeSheetStatus[]=3) */
  filters?: Record<string, string | number | (string | number)[] | null | undefined>;
  /** Clés dans l'ordre de priorité du tri */
  order?: Record<string, SortDirection>;
  page: number;
  itemsPerPage: number;
  /**
   * Pagination partielle (partial=true) : l'API ne compte pas les résultats (pas de COUNT, pas de totalItems). Pour les
   * listes qui n'affichent pas de total (ex. Historique : 12 périodes au plus).
   */
  partial?: boolean;
}

export interface Page<M> {
  models: M[];
  /** Nombre total de résultats, toutes pages confondues (totalItems de l'API) ; en partiel, ceux reçus seulement */
  totalItems: number;
}

/**
 * Charge une seule page d'une ressource et son total.
 * Collection.list() de model-core charge toutes les pages et ne remonte pas totalItems : d'où ce helper, en attendant
 * une évolution de model-core (cf. model-core/PROPOSITION-pagination.md, non poussé).
 */
export async function listPage<T extends ModelConstructor>(
  modelClass: T,
  request: PageRequest,
  /** Relations à résoudre avec la page (resolveRelations), ex. [['member', 'user'], ['teamMember', 'team']] */
  resolvables: readonly ResolvablePath[] = [],
  /** Autre chargement de la page, en même temps que les relations (ex. ses résumés : fetchSummaries) */
  alongside?: (models: InstanceType<T>[]) => Promise<void>,
): Promise<Page<InstanceType<T>>> {
  const model = new modelClass();
  const query = qs.stringify(
    {
      ...Object.fromEntries(Object.entries(request.filters ?? {}).filter(([, value]) => value !== '' && value != null)),
      order: request.order,
      page: request.page,
      itemsPerPage: request.itemsPerPage,
      partial: request.partial || undefined,
    },
    { arrayFormat: 'brackets', encodeValuesOnly: true },
  );
  const { data } = await model.connector.fetch(`${model.fullPath}?${query}`, { silentError: true });
  const body = (data ?? {}) as { member?: ModelAttributes[]; totalItems?: number };
  const members = body.member ?? [];

  const models = members.map((attributes) => new modelClass(attributes) as InstanceType<T>);
  await Promise.all([resolveRelations(models, resolvables), alongside?.(models)]);

  return { models, totalItems: body.totalItems ?? members.length };
}
